import { DatePicker, DatePickerProps, Range } from '@shopify/polaris';
import { CalendarTickMajor } from '@shopify/polaris-icons';
import { formatDateTime } from '@utils/dates';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { SpaceBetweenRowContainer } from '../container/common';
import PickerButton, { DatePickerContainer } from './common';
import TimePicker, { nearestTimeValueInFuture, Time } from './time-picker';

const MainContainer = styled(SpaceBetweenRowContainer)`
  flex: wrap;
  ${DatePickerContainer}:last-child {
    margin: 0;
  }
`;

export type DateTimePickerProps = {
  disabled?: boolean;
  dateTitle: string;
  initialValue: Date;
  onChange: (date: Date) => void;
  timeSteps?: number;
  timeTitle: string;
} & Omit<
  DatePickerProps,
  'month' | 'onChange' | 'onMonthChange' | 'selected' | 'year'
>;

export const nearestDateTime = (date: Date, timeSteps: number) => {
  const { time } = formatDateTime(date);
  const { hours, minutes } = nearestTimeValueInFuture(time, timeSteps);
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours,
    minutes,
  );
};

const DateTimePicker: FunctionComponent<DateTimePickerProps> = ({
  dateTitle,
  disabled,
  initialValue,
  onChange,
  timeSteps = 30,
  timeTitle,
  ...datePickerProps
}) => {
  const now = new Date();
  const [{ month, year }, setMonthYear] = useState({
    month: now.getMonth(),
    year: now.getFullYear(),
  });
  const [dateTime, setDateTime] = useState(initialValue);

  useEffect(() => {
    setDateTime(initialValue);
  }, [initialValue]);

  const onChangeDateTime = useCallback(
    (date: Date) => {
      setDateTime(date);
      onChange(date);
    },
    [onChange],
  );

  const onChangeDate = useCallback(
    (range: Range) => {
      onChangeDateTime(
        new Date(
          range.start.getFullYear(),
          range.start.getMonth(),
          range.start.getDate(),
          dateTime.getHours(),
          dateTime.getMinutes(),
        ),
      );
    },
    [dateTime, onChangeDateTime],
  );

  const onChangeTime = useCallback(
    (time: Time) => {
      onChangeDateTime(
        new Date(
          dateTime.getFullYear(),
          dateTime.getMonth(),
          dateTime.getDate(),
          time.hours,
          time.minutes,
        ),
      );
    },
    [dateTime, onChangeDateTime],
  );

  const handleMonthChange = useCallback(
    (month, year) => setMonthYear({ month, year }),
    [],
  );

  const dateTimeFormatted = formatDateTime(dateTime);
  return (
    <MainContainer>
      <PickerButton
        icon={{
          source: CalendarTickMajor,
        }}
        title={dateTitle}
        value={dateTimeFormatted.date}
        disabled={disabled}
      >
        <DatePicker
          {...datePickerProps}
          month={month}
          year={year}
          onChange={onChangeDate}
          onMonthChange={handleMonthChange}
          selected={dateTime}
        />
      </PickerButton>
      <TimePicker
        date={initialValue}
        disabled={disabled}
        title={timeTitle}
        value={dateTimeFormatted.time}
        onChange={onChangeTime}
        step={timeSteps}
      />
    </MainContainer>
  );
};

export default DateTimePicker;
